// Phase 35e — AI Portfolio Pulse card on the Dashboard.
//
// One ambient card under the Dashboard hero. Pulls from
// /api/portfolio/commentary (24h cached server-side) and shows:
//   • Headline (bold one-liner)
//   • 3-5 short observations as bullets
//   • A single CTA-styled suggested action
//   • A subtle Refresh button (admin-only refresh actually triggers an
//     LLM call; non-admin users get a 24h cache hit anyway)
//
// Soft-degrades to a fallback message on error. Never blocks the rest
// of the dashboard from rendering.

import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function PortfolioPulseCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPulse = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = refresh
        ? "/portfolio/commentary?refresh=true"
        : "/portfolio/commentary";
      const { data: payload } = await api.get(url);
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || "Pulse unavailable right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPulse(false);
  }, [fetchPulse]);

  // Skeleton on very first load.
  if (loading && !data) {
    return (
      <section
        data-testid="dashboard-ai-commentary"
        className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-violet-50/30 p-5 shadow-sm"
      >
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
          <span className="text-sm text-slate-500">Reading your portfolio…</span>
        </div>
      </section>
    );
  }

  if (error || !data) return null;

  const isFallback = (data.source || "").startsWith("fallback_");

  return (
    <section
      data-testid="dashboard-ai-commentary"
      data-source={data.source}
      className="overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-violet-50/30 shadow-sm"
    >
      <header className="flex items-center justify-between gap-3 border-b border-indigo-100 bg-white/60 px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              📊 AI Portfolio Pulse
            </p>
            <p className="text-[11px] text-slate-500">
              {isFallback ? "Quick read" : "Refreshed daily by Haiku"}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchPulse(true)}
          disabled={loading}
          data-testid="dashboard-pulse-refresh"
          className="text-indigo-600 hover:bg-indigo-50"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </header>
      <div className="px-5 py-4">
        <p
          className="mb-3 text-base font-semibold text-slate-900"
          data-testid="dashboard-pulse-headline"
        >
          {data.headline}
        </p>
        <ul className="mb-4 space-y-1.5" data-testid="dashboard-pulse-observations">
          {(data.observations || []).map((obs, i) => (
            <li
              key={i}
              className="flex gap-2 text-sm text-slate-700"
              data-testid={`dashboard-pulse-observation-${i}`}
            >
              <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-indigo-400" />
              <span>{obs}</span>
            </li>
          ))}
        </ul>
        {data.suggested_action && (
          <div
            className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white"
            data-testid="dashboard-pulse-action"
          >
            <ArrowRight className="h-3 w-3" />
            {data.suggested_action}
          </div>
        )}
      </div>
    </section>
  );
}
